import { useEffect, useMemo, useState, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import permissionDeniedLogService, { PermissionDeniedRow } from '../../services/permissionDeniedLog.service';

const formatTimestamp = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

const PermissionErrors: React.FC = () => {
  const [rows, setRows] = useState<PermissionDeniedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await permissionDeniedLogService.getRecent();
      setRows(data.rows);
      setLastLoadedAt(new Date());
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load permission errors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const columns = useMemo<ColumnDef<PermissionDeniedRow>[]>(() => [
    {
      id: 'created_at',
      accessorKey: 'created_at',
      header: 'When',
      cell: ({ row }) => formatTimestamp(row.original.created_at),
      size: 170,
    },
    {
      id: 'user_email',
      accessorKey: 'user_email',
      header: 'User',
      size: 220,
    },
    {
      id: 'method',
      accessorKey: 'method',
      header: 'Method',
      size: 90,
    },
    {
      id: 'path',
      accessorKey: 'path',
      header: 'Path',
      size: 320,
    },
    {
      id: 'page_url',
      accessorKey: 'page_url',
      header: 'Page',
      cell: ({ row }) => row.original.page_url || '',
      size: 280,
    },
    {
      id: 'message',
      accessorKey: 'message',
      header: 'Message',
      cell: ({ row }) => row.original.message || '',
      size: 360,
    },
    {
      id: 'ip_address',
      accessorKey: 'ip_address',
      header: 'IP',
      cell: ({ row }) => row.original.ip_address || '',
      size: 130,
    },
    {
      id: 'user_agent',
      accessorKey: 'user_agent',
      header: 'User Agent',
      cell: ({ row }) => row.original.user_agent || '',
      size: 300,
    },
  ], []);

  return (
    <div className="h-full w-full p-10 pb-24">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-heading">Permission Errors</h1>
          <p className="text-sm text-muted">
            Permission-denied events surfaced to users in the last 30 days (up to 5,000 rows).
            {lastLoadedAt && ` Loaded ${lastLoadedAt.toLocaleTimeString()}.`}
          </p>
        </div>
        <button
          onClick={loadRows}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="permission-errors-table"
        fieldName="permission-errors-table"
        searchPlaceholder="Search by user, path, message..."
        enableGrouping={true}
        enableColumnFilters={true}
        cardTitle={`Permission Errors (${rows.length})`}
      />
    </div>
  );
};

export default PermissionErrors;
